import { RootCauseTriggerKey } from './ImmuneInflammatory';

/**
 * Practitioner reference content for the Root Cause Framework — the Assessment /
 * Testing / Intervention bullets for each of the four triggers, transcribed from
 * the BWH Labs I³ Practitioner Quick Reference (Rountree/Strand). Rendered in the
 * Assessment / Testing / Intervention tabs beside the score panels.
 *
 * NOTE: This is the flowchart's page-1 clinical guidance. The page-2 targeted
 * supplement reference (BWH-85 β-glucan, barrier table) remains out of scope.
 */
export interface RootCauseGuidance {
  key: RootCauseTriggerKey;
  label: string;
  subtitle: string;
  assessment: string[];
  testing: string[];
  intervention: string[];
}

export const ROOT_CAUSE_GUIDANCE: RootCauseGuidance[] = [
  {
    key: 'microbes',
    label: 'Microbes',
    subtitle: 'Oral · Gut · Lung · Skin · Genitourinary',
    assessment: [
      'Assess microbial burden at all barriers: oral, gut, lung, skin, or genitourinary',
      'Symptoms spanning systems: GI, urinary, respiratory, oral, or skin',
      'History of antibiotics, low-diversity diet, or high sugar',
      'Symptoms worsen after meals',
      'Symptomatic improvement or worsening after antibiotics',
    ],
    testing: [
      'Stool microbiome, organic acid panel, breath test for SIBO if warranted',
      'DNA sequencing or PCR from relevant bodily fluid or skin swab',
    ],
    intervention: [
      'Targeted antimicrobials: botanical or Rx',
      'Probiotics / prebiotics / postbiotics',
      'Digestive support',
      'Gut healing protocol',
      'Reduce refined carbohydrates and sugars',
      'Increase dietary fiber',
    ],
  },
  {
    key: 'foods',
    label: 'Foods',
    subtitle: 'IgE allergy · IgG intolerance · Histamine · Pro-inflammatory diet',
    assessment: [
      'Food reactions — immediate or delayed hours to days',
      'Gut, skin, or brain fog aggravated after eating',
      'Narrowing food tolerance over time',
      'Histamine-rich foods trigger symptoms',
      'Regular ultra-processed foods, dyes, additives, deep-fried foods, or trans-fats',
      'Suspected intolerances: lactose, fructose, oxalates, etc.',
    ],
    testing: [
      'Elimination diet or food sensitivity panel',
      'Screen for: histamine intolerance, lactose, fructose, oxalates, lectins, FODMAPs',
    ],
    intervention: [
      'Eliminate ultra-processed foods, artificial additives, dyes, and trans-fats',
      'Gut barrier support',
      'Address histamine: low-histamine diet, anti-histamine nutrients and herbs',
    ],
  },
  {
    key: 'toxins',
    label: 'Toxins',
    subtitle: 'Mold/mycotoxins · Heavy metals · Pesticides · Endocrine disruptors',
    assessment: [
      'Mold or water-damaged building exposure (current or past)',
      'Dental amalgams, high fish consumption',
      'Occupational chemical exposure',
      'Symptoms worse in specific buildings or environments',
    ],
    testing: [
      'Environmental toxicant panel (mycotoxins, pesticides, VOCs)',
      'Heavy metal testing (blood and urine)',
      'Organic acid panel',
    ],
    intervention: [
      'Avoid: reduce/eliminate source exposure — highest priority',
      'Mitigate: antioxidant and anti-inflammatory support; biotransformation — phase 1, 2, and 3 liver pathways',
      'Eliminate: binders, drainage support, sauna, purified water',
    ],
  },
  {
    key: 'trauma',
    label: 'Trauma',
    subtitle: 'Psychological · ACEs · Chronic stress · Autonomic dysregulation',
    assessment: [
      'Chronic stress, trauma history (ACEs), or current psychological diagnosis',
      'Symptoms worsen or flare with life stress, poor sleep, or relationship conflict',
      'Signs of HPA dysregulation: burnout, anxiety, mood shifts, cortisol pattern changes',
    ],
    testing: ['Validated stress / trauma screen (ACE score, PHQ-9, GAD-7)'],
    intervention: [
      'Referral: trauma-informed therapist, counselor, or integrative psychiatrist',
      'Adaptogenic support; HPA axis support',
      'Neurotransmitter support',
      'Mind-body: HRV biofeedback, breathwork, somatic movement, yoga therapy',
    ],
  },
];

export type RootCauseGuidanceTab = 'assessment' | 'testing' | 'intervention';

export const ROOT_CAUSE_GUIDANCE_TABS: { key: RootCauseGuidanceTab; label: string }[] = [
  { key: 'assessment', label: 'Assessment' },
  { key: 'testing', label: 'Testing' },
  { key: 'intervention', label: 'Intervention' },
];
